import React, { useContext, useEffect, useState } from 'react';

import { Form, Formik } from 'formik';
import DOMPurify from 'isomorphic-dompurify';
import { useRouter } from 'next/router';
import { useToasts } from 'react-toast-notifications';

import CustomDatePicker from '@components/common/CustomDatePicker/CustomDatePicker';
import Select from '@components/common/Dropdowns/Select/Select';
import EditConfirmIcon from '@components/common/EditConfirmIcon/EditConfirmIcon';
import EditIcon from '@components/common/EditIcon/EditIcon';
import Editor from '@components/common/editor/Editor';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import IconCheckmark from '@components/common/IconCheckmark/IconCheckmark';
import ConfirmModal from '@components/common/Modals/ConfirmModal/ConfirmModal';
import Text from '@components/common/Text/Text';
import { MAX_LENGTH_DESCRIPTION } from '@components/lpc/Constants';
import PotContext from '@components/lpc/Context/Pot/PotContext';
import DescriptionGeneratorModal from '@components/lpc/Pot/CreatePot/LeftSides/LeftSideLPS/DescriptionGeneratorModal/DescriptionGeneratorModal';
import EmptyDescription from '@components/lpc/Pot/MainModule/Informations/Description/BannerMessage/EmptyDescription';
import DisabledInput from '@components/lpc/Pot/MainModule/Informations/DisabledInput/DisabledInput';
import { createPotCategoryModel, subCategoryLPSEnum } from '@models/PotModel/createPotCategoryModel';
import { personRoles } from '@models/PotModel/personRoles';
import potService from '@services/domain/Lpc/PotService';
import { ROUTE } from '@services/http/Route';
import { formatDateToInternational } from '@services/utils/DateService';
import { getTextFromHtml } from '@services/utils/StringService';
import { getCategoryIconSrc, isSupportedCategory } from '@services/utils/UserService';

import css from './Description.module.scss';

type DescriptionProps = {
    category: string;
    solidaryCategory?: string;
    eventDate?: string;
    description: string;
    potRef: string;
    personRole: string;
    isOffered: boolean;
};
const Description = ({
    category,
    solidaryCategory,
    eventDate,
    description,
    potRef,
    personRole,
    isOffered,
}: DescriptionProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'common', 'lpc-create-pot']);
    const { addToast } = useToasts();
    const router = useRouter();
    const now = new Date().getUTCFullYear();
    const years = Array(5)
        .fill('')
        .map((_, index) => now + index);
    const getListCategories = (
        isSupportedCategory(category)
            ? createPotCategoryModel
            : [...createPotCategoryModel, { value: category, label: category }]
    ).filter((item) => item.value !== 'SOLIDARITY_POOL');
    const potContext = useContext(PotContext);
    const { getUpdatedPot } = potContext;

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [initialCategory, setInitialCategory] = useState<string>(category);
    const [initialSolidaryCategory, setInitialSolidaryCategory] = useState<string>(solidaryCategory);
    const [listRedirectionModalVisible, setListRedirectionModalVisible] = useState<boolean>(false);
    const canNotUpdateCategory = category === 'MPP' || category === 'TEACHER_GIFT';

    const [isDescriptionGeneratorModalVisible, setIsDescriptionGeneratorModalVisible] = useState<boolean>(false);
    const [editorKey, setEditorKey] = useState<number>(0);
    const solidaryCategories = subCategoryLPSEnum.map((item) => {
        return {
            value: item,
            label: t(`form.solidarity-pool.categories.${item}`),
        };
    });
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const toggleText = () => {
        setIsExpanded(!isExpanded);
    };
    const getInitialValues = () => {
        return {
            eventDate: eventDate ? new Date(eventDate) : '',
            description: description,
        };
    };

    const onSubmit = async (values) => {
        const body = {
            eventDate: values.eventDate ? formatDateToInternational(values.eventDate, '-') : undefined,
            desc: values.description,
            category: initialCategory,
            solidaryCategory: initialSolidaryCategory,
        };

        setIsLoading(true);
        potService
            .updatePot(potRef, body)
            .then((res) => res.status === 200 && getUpdatedPot(potRef))
            .then(() => {
                getUpdatedPot(potRef);
                addToast(t(`success.UPDATE_SUCCESS`), {
                    appearance: 'success',
                    autoDismiss: true,
                });
            })
            .catch((err) =>
                addToast(t(`errors.${err.code}`), {
                    appearance: 'error',
                    autoDismiss: true,
                }),
            )
            .finally(() => {
                setIsEditing(false);
                setIsLoading(false);
            });
    };

    const onEdit = () => {
        setIsEditing(true);
    };

    const potCategoriesList = (category) => {
        return (
            <div className={css.information_form__select__category}>
                {isSupportedCategory(category) && <img src={getCategoryIconSrc(category)} alt={category} />}
                {t(`category.${category}`)}
            </div>
        );
    };

    const getDescription = () => {
        return isExpanded
            ? description
            : description.substring(0, MAX_LENGTH_DESCRIPTION) +
                  (description.length > MAX_LENGTH_DESCRIPTION ? '...' : '');
    };

    const renderForm = (values, setFieldValue) => {
        return (
            <Form>
                <div className={css.information_form__title}>
                    <h5>{t('informations.description-label')}</h5>
                    <EditConfirmIcon
                        isLoading={isLoading}
                        onClose={() => setIsEditing(false)}
                        onConfirm={() => onSubmit(values)}
                        isDisabled={
                            (!values.description || getTextFromHtml(values.description).length < 10) &&
                            initialCategory === 'SOLIDARITY_POOL'
                        }
                    />
                </div>
                <div className={css.information_form__input}>
                    {category === 'SOLIDARITY_POOL' && (
                        <div
                            onClick={() => setIsDescriptionGeneratorModalVisible(true)}
                            className={css.information_form__custom_ai_editor}
                        >
                            <img height={14} src="/front-static/icons/pot/stars.png" alt="stars" />
                            <Text variant={'caption_bold_small'} color={'brand-primary'}>
                                {t('form.solidarity-pool.ai-editor-cta')}
                            </Text>
                        </div>
                    )}
                    <Editor
                        key={editorKey}
                        value={values.description}
                        editorName="description"
                        editorImageUploadUrl={'pot'}
                        editorPlaceholder={t(`category-description.desc_pot_${category}`)}
                        editorId="description"
                    />
                    <DescriptionGeneratorModal
                        isVisible={isDescriptionGeneratorModalVisible}
                        onHide={() => setIsDescriptionGeneratorModalVisible(false)}
                        onAppendDescription={(description) => {
                            setFieldValue('description', description);
                            setEditorKey(editorKey + 1);
                        }}
                        title={values.name}
                        category={initialSolidaryCategory}
                    ></DescriptionGeneratorModal>
                </div>
                <div className={css.information_form__input__half}>
                    {category === 'SOLIDARITY_POOL' ? (
                        <Select
                            options={solidaryCategories}
                            onSelect={(value) => setInitialSolidaryCategory(value)}
                            value={initialSolidaryCategory}
                            customSelect={css.information_form__select}
                            customMenu={css.information_form__select__menu}
                            customControl={css.information_form__select__control}
                            customWrapper={css.information_form__select__wrapper}
                            label={t('informations.category-label')}
                            placeholder={t(`select.pot-category.${category}`)}
                        />
                    ) : (
                        <Select
                            options={getListCategories.map((item) => {
                                return {
                                    value: item.value,
                                    label: potCategoriesList(item.value),
                                };
                            })}
                            onSelect={(value) => setInitialCategory(value)}
                            value={initialCategory}
                            customSelect={css.information_form__select}
                            customMenu={css.information_form__select__menu}
                            customControl={css.information_form__select__control}
                            customWrapper={css.information_form__select__wrapper}
                            customArrowClosed={canNotUpdateCategory ? css.information_form__select__arrow : ''}
                            label={t('informations.category-label')}
                            placeholder={t(`select.pot-category.${category}`)}
                            disabled={canNotUpdateCategory}
                        />
                    )}
                    {category !== 'SOLIDARITY_POOL' && (
                        <div className={css.information_form__input__date}>
                            <CustomDatePicker
                                years={years}
                                handleDateChange={setFieldValue}
                                label={t('informations.event-date-label')}
                                placeholder={t('form.event-date-placeholder')}
                                name="eventDate"
                                value={values.eventDate}
                                minDate={new Date()}
                            />
                        </div>
                    )}
                </div>
            </Form>
        );
    };

    useEffect(() => {
        const isList = initialCategory === 'WEDDING_PACS_GIFT' || initialCategory === 'BIRTH_GIFT';

        if (isList) {
            setListRedirectionModalVisible(true);
        }
    }, [initialCategory]);

    return (
        <>
            <ConfirmModal
                onHide={() => {
                    setListRedirectionModalVisible(false);
                    setInitialCategory(category);
                }}
                onConfirm={() => router.push(ROUTE.WEDDING_LIST)}
                title={t(`modal.${initialCategory}.forbidden-title`)}
                text={t(`modal.${initialCategory}.forbidden-text`)}
                confirmLabel={t(`modal.${initialCategory}.redirect-confirm`)}
                cancelLabel={t(`modal.${initialCategory}.redirect-cancel`)}
                isVisible={listRedirectionModalVisible}
                iconComponent={<IconCheckmark variant={'list'} />}
                customIcon={css.modal}
            />
            {personRole !== personRoles.BENEFICIARY ? (
                <div
                    className={`${personRole == personRoles.PARTICIPANT && css.information_form_part} ${css.information_form}`}
                >
                    {!isEditing ? (
                        <>
                            <div className="DescriptionMobile">
                                <div className={css.information_form__title}>
                                    <h5>{t('informations.description-label')}</h5>
                                    {personRole === personRoles.ORGANISER && !isOffered && description && (
                                        <EditIcon onEdit={onEdit} />
                                    )}
                                </div>
                            </div>
                            <div className={css.disabled}>
                                {personRole === personRoles.ORGANISER && !isOffered && !description && (
                                    <EmptyDescription onEdit={onEdit} />
                                )}
                                <div className={css.disabled__editor}>
                                    {description && (
                                        <>
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(getDescription()),
                                                }}
                                                className={css.disabled__description}
                                            />
                                            {description.length > MAX_LENGTH_DESCRIPTION && (
                                                <a onClick={toggleText} className={css.disabled__read_more}>
                                                    {isExpanded
                                                        ? t('informations.read-less')
                                                        : t('informations.read-more')}
                                                </a>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className={css.disabled__input}>
                                    {category !== 'SOLIDARITY_POOL' ? (
                                        <DisabledInput type={'category'} category={initialCategory}>
                                            {t(`category.${initialCategory}`)}
                                        </DisabledInput>
                                    ) : (
                                        <DisabledInput type={'category'} category={initialSolidaryCategory}>
                                            {t(`form.solidarity-pool.categories.${initialSolidaryCategory}`)}
                                        </DisabledInput>
                                    )}
                                    {eventDate && <DisabledInput type={'date'}>{eventDate}</DisabledInput>}
                                </div>
                            </div>
                        </>
                    ) : (
                        <Formik initialValues={getInitialValues()} onSubmit={(values) => onSubmit(values)}>
                            {({ values, setFieldValue }) => renderForm(values, setFieldValue)}
                        </Formik>
                    )}
                </div>
            ) : (
                <div className={css.beneficiary}>
                    <img
                        className={css.beneficiary__quote__start}
                        src="/front-static/icons/display/quotation-start.svg"
                        alt="quotation"
                    />
                    <Text
                        variant={'body_01'}
                        htmlText={getDescription()}
                        color={'txt-primary'}
                        html
                        customClass={css.beneficiary__description}
                    />
                    {description.length > MAX_LENGTH_DESCRIPTION && (
                        <a onClick={toggleText} className={css.beneficiary__read_more}>
                            {isExpanded ? t('informations.read-less') : t('informations.read-more')}
                        </a>
                    )}
                    <img
                        className={css.beneficiary__quote__end}
                        src="/front-static/icons/display/quotation-end.svg"
                        alt="quotation"
                    />
                </div>
            )}
        </>
    );
};
export default Description;
